import { useEffect, useCallback } from 'react';
import { hasMarketingConsent, hasAnalyticsConsent } from './CookieConsent';

/**
 * RetargetingPixels Component
 *
 * Handles loading of Google Ads (remarketing) and Meta (Facebook) pixels
 * for retargeting visitors across the web.
 *
 * IMPORTANT: These scripts only load if user has consented to marketing cookies.
 *
 * To set up:
 * 1. Google Ads: Get your conversion ID from Google Ads dashboard
 *    - Go to Tools & Settings > Conversions > Website
 *    - Create a new conversion action or use existing
 *    - Copy the Conversion ID (e.g., AW-XXXXXXXXXX)
 *
 * 2. Meta/Facebook Pixel: Get your Pixel ID from Meta Business Suite
 *    - Go to Events Manager > Data Sources > Pixels
 *    - Create or select your pixel
 *    - Copy the Pixel ID (e.g., XXXXXXXXXXXXXXX)
 *
 * Environment Variables (add to .env):
 * REACT_APP_GOOGLE_ADS_ID=AW-XXXXXXXXXX
 * REACT_APP_META_PIXEL_ID=XXXXXXXXXXXXXXX
 */

// Configuration - Set these in .env file
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

function RetargetingPixels({ cookieConsent }) {
  // Load Google Tag Manager / Google Ads
  const loadGoogleAds = useCallback(() => {
    if (!GOOGLE_ADS_ID || window.gtag) return;

    // Load gtag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      gtag('js', new Date());

      // Configure Google Ads
      gtag('config', GOOGLE_ADS_ID, {
        'allow_google_signals': true,
        'allow_ad_personalization_signals': true
      });

      // If we also have GA measurement ID, configure that too
      if (GA_MEASUREMENT_ID) {
        gtag('config', GA_MEASUREMENT_ID, {
          'anonymize_ip': true,
          'cookie_flags': 'SameSite=None;Secure'
        });
      }

      console.log('[Retargeting] Google Ads loaded:', GOOGLE_ADS_ID);
    };
  }, []);

  // Load Meta (Facebook) Pixel
  const loadMetaPixel = useCallback(() => {
    if (!META_PIXEL_ID || window.fbq) return;

    // Meta Pixel base code
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');

    console.log('[Retargeting] Meta Pixel loaded:', META_PIXEL_ID);
  }, []);

  // Initialize pixels based on consent
  useEffect(() => {
    const hasMarketing = cookieConsent?.marketing || hasMarketingConsent();
    const hasAnalytics = cookieConsent?.analytics || hasAnalyticsConsent();

    if (hasMarketing) {
      loadGoogleAds();
      loadMetaPixel();
    } else if (hasAnalytics && GA_MEASUREMENT_ID) {
      // Load only analytics (Google Analytics) without ads features
      if (!window.gtag) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);

        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            window.dataLayer.push(arguments);
          }
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', GA_MEASUREMENT_ID, {
            'anonymize_ip': true,
            'allow_google_signals': false,
            'allow_ad_personalization_signals': false
          });
          console.log('[Retargeting] Google Analytics loaded (analytics only):', GA_MEASUREMENT_ID);
        };
      }
    }
  }, [cookieConsent, loadGoogleAds, loadMetaPixel]);

  // Listen for consent changes
  useEffect(() => {
    const handleConsentChange = (event) => {
      const prefs = event.detail;
      if (prefs.marketing) {
        loadGoogleAds();
        loadMetaPixel();
      }
    };

    window.addEventListener('cookieConsentUpdated', handleConsentChange);
    return () => window.removeEventListener('cookieConsentUpdated', handleConsentChange);
  }, [loadGoogleAds, loadMetaPixel]);

  // This component doesn't render anything visible
  return null;
}

/**
 * Track custom events for retargeting
 * Call these functions from your components when users perform key actions
 */

// Track when user views an itinerary
export function trackViewItinerary(destination, duration) {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'view_itinerary', {
      'destination': destination,
      'trip_duration': duration,
      'send_to': GOOGLE_ADS_ID
    });
  }
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: destination,
      content_category: 'itinerary',
      value: duration
    });
  }
}

// Track when user creates an itinerary
export function trackCreateItinerary(destination, duration) {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_ID}/create_itinerary`,
      'destination': destination,
      'trip_duration': duration
    });
  }
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: destination,
      content_category: 'itinerary_created',
      value: 1
    });
  }
}

// Track when user signs up
export function trackSignUp(method = 'email') {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'sign_up', {
      'method': method,
      'send_to': GOOGLE_ADS_ID
    });
  }
  if (window.fbq) {
    window.fbq('track', 'CompleteRegistration', {
      content_name: method,
      status: true
    });
  }
}

// Track when user subscribes (conversion)
export function trackSubscribe(plan, value, currency = 'USD') {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'conversion', {
      'send_to': `${GOOGLE_ADS_ID}/subscribe`,
      'value': value,
      'currency': currency,
      'transaction_id': Date.now().toString()
    });
  }
  if (window.fbq) {
    window.fbq('track', 'Subscribe', {
      value: value,
      currency: currency,
      predicted_ltv: value * 12
    });
  }
}

// Track when user views blog article
export function trackViewBlogArticle(title, category, destination) {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'view_blog', {
      'article_title': title,
      'category': category,
      'destination': destination,
      'send_to': GOOGLE_ADS_ID
    });
  }
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: title,
      content_category: category || 'blog',
      content_type: 'article'
    });
  }
}

// Track page views (call on route changes)
export function trackPageView(pagePath, pageTitle) {
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
}

// Track a click through to a booking partner.
//
// This is the highest-intent signal the site produces on anonymous traffic: the
// visitor got as far as looking at where they would sleep. Mapped to
// InitiateCheckout / begin_checkout rather than a generic event so it lands in
// the audiences the ad platforms already optimise for.
export function trackBookingClick(destination, linkType = 'hotel', partner = 'booking') {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'begin_checkout', {
      'destination': destination,
      'link_type': linkType,
      'partner': partner,
      'send_to': GOOGLE_ADS_ID
    });
  }
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: destination,
      content_category: `booking_${linkType}`,
      content_type: 'travel'
    });
  }
}

// Add user to custom audience (for more targeted retargeting)
export function trackUserInterest(interest) {
  if (window.gtag && GOOGLE_ADS_ID) {
    window.gtag('event', 'user_interest', {
      'interest': interest,
      'send_to': GOOGLE_ADS_ID
    });
  }
  if (window.fbq) {
    window.fbq('trackCustom', 'UserInterest', {
      interest: interest
    });
  }
}

export default RetargetingPixels;
